import React from 'react';
import { Formik, Form, ErrorMessage } from 'formik';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';

import { colourStylesFullWidth } from '../../data/Constants';
import FormInputLayout from '../FormInputLayout';
import PlainButton from '../buttons/PlainButton';
import { AppealStatus, AppealStatusLabelandValues } from '../../data/AppealsConstants';
import DatePicker from 'react-multi-date-picker';
import moment from 'moment';

const BulkAppealStatusUpdate = ({ closePopup, handleSubmit }) => {
    const dispatch = useDispatch();
    const status = useSelector((state) => state.general.firstSelectedStatus);

    // Function to filter options based on status
    const getOptionsByStatus = (status) => {
        if (!status) {
            console.warn('Status is undefined or null');
            return [];
        }

        switch (status) {
            case AppealStatus.PENDING:
                return AppealStatusLabelandValues.filter(
                    (option) => option.label === "Pending For Appeal Board Decision"
                );

            case AppealStatus.PENDING_FOR_APPEAL_BOARD_DECISION:
                return AppealStatusLabelandValues.filter(
                    (option) => ["Approved", "Rejected"].includes(option.label)
                );

            default:
                return AppealStatusLabelandValues; // Show all options if no specific status
        }
    };

    const capitalizeWords = (str) =>
        str
            .replace(/_/g, " ") // Replace underscores with spaces
            .toLowerCase() // Convert all to lowercase
            .replace(/\b\w/g, (char) => char.toUpperCase());

    const showAllStatus = Object.entries(AppealStatus).map(([key, value]) => ({
        label: capitalizeWords(key), // Capitalize each word
        value: value,
    }));

    const initialValues = {
        status: showAllStatus.find((option) => option.value === status) || null,
        approval_date: "",
        rejection_date: "",
    };

    return (
        <Formik
            initialValues={initialValues} // Set initial status value from showAllStatus
            onSubmit={handleSubmit}
        >
            {({ values, setFieldValue }) => (
                <Form className='stepper-form-container'>
                    <FormInputLayout label="Status" formikName="status" required={false}>
                        <Select
                            isSearchable={false}
                            styles={colourStylesFullWidth}
                            options={getOptionsByStatus(status)} // Use the filtered options based on status
                            onChange={(valueObj) => {
                                const newValue = valueObj?.value;
                                // Update Formik status
                                setFieldValue("status", newValue);
                                if (newValue !== AppealStatus.APPROVED) {
                                    setFieldValue("approval_date", ""); // Clear approval date if not approved
                                }
                                if (newValue !== AppealStatus.REJECTED) {
                                    setFieldValue("rejection_date", ""); // Clear rejection date if not rejected
                                }
                                // Dispatch action to update Redux status if needed
                                dispatch({ type: 'UPDATE_STATUS_ACTION', payload: newValue }); // Replace with your actual action
                            }}
                            value={showAllStatus.find((option) => option.value === values.status)} // Use Formik's value
                            components={{ IndicatorSeparator: () => null }}
                        />
                    </FormInputLayout>
                    {values.status === AppealStatus.APPROVED && (
                        <FormInputLayout label="Board Approval Date" formikName="approval_date" required={false}>
                            <DatePicker
                                value={values.approval_date ? moment(values.approval_date).toDate() : null}
                                onChange={(date) => {
                                    const formattedDate = date ? moment(date.toDate()).toISOString() : "";
                                    setFieldValue("approval_date", formattedDate);
                                }}
                                placeholderText="Approval Date"
                                format="DD MMMM YYYY"
                                months={["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]}
                            />
                            <ErrorMessage name="approval_date" component="div" className="text-danger fs-14 fw-400 mt-1" />
                        </FormInputLayout>
                    )}

                    {values.status === AppealStatus.REJECTED && (
                        <FormInputLayout label="Board Rejection Date" formikName="rejection_date" required={false}>
                            <DatePicker
                                value={values.rejection_date ? moment(values.rejection_date).toDate() : null}
                                onChange={(date) => {
                                    const formattedDate = date ? moment(date.toDate()).toISOString() : "";
                                    setFieldValue("rejection_date", formattedDate);
                                }}
                                placeholderText="Rejection Date"
                                format="DD MMMM YYYY"
                                months={["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]}
                            />
                            <ErrorMessage name="rejection_date" component="div" className="text-danger fs-14 fw-400 mt-1" />
                        </FormInputLayout>
                    )}

                    <div className="d-flex justify-content-end gap-4">
                        <button type="button" className="cancel-btn" onClick={closePopup}>
                            Cancel
                        </button>
                        <PlainButton type="submit">
                            Confirm
                        </PlainButton>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default BulkAppealStatusUpdate;
